import { useState, useCallback } from 'react';
import { message } from 'antd';
import { j209Api, FJ180, FJ181 } from '../../../services/j209Api';
import { useFieldErrors } from '../../../hooks/useFieldErrors';
import { Mode } from './useJ209Mode';

export function useJ209Record() {
  const [record, setRecord] = useState<FJ180 | null>(null);
  const [formData, setFormData] = useState<Partial<FJ180>>({});
  const [details, setDetails] = useState<Partial<FJ181>[]>([]);
  const [selectedRow, setSelectedRow] = useState(0);
  const [loading, setLoading] = useState(false);
  const { clearFieldErrors, getFieldError, setFieldErrors } = useFieldErrors();

  const loadRecord = useCallback(async (id: string): Promise<{
    data: FJ180;
    dets: Partial<FJ181>[];
  } | null> => {
    setLoading(true);
    try {
      const data = await j209Api.get(id);
      setRecord(data);
      setFormData({ ...data });
      const dets = data.details || [];
      setDetails(dets);
      setSelectedRow(0);
      return { data, dets };
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const setField = useCallback((key: keyof FJ180, value: FJ180[keyof FJ180]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  const getField = useCallback((key: keyof FJ180): FJ180[keyof FJ180] => {
    return (formData as Record<string, FJ180[keyof FJ180]>)[key] ?? '';
  }, [formData]);

  const handleSave = useCallback(async (
    mode: Mode,
    onSuccess: (result: FJ180) => void,
  ) => {
    setLoading(true);
    try {
      const req = {
        master: formData as FJ180,
        details: details as FJ181[],
      };
      if (mode === 'add') {
        const result = await j209Api.create(req);
        message.success(`新增成功: ${result.fj180_01}`);
        onSuccess(result);
      } else {
        const result = await j209Api.update(formData.fj180_01!, req);
        message.success('修改成功');
        setRecord(result);
        setFormData({ ...result });
        setDetails(result.details || []);
        onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        setFieldErrors(errors);
        const summary = errors.slice(0, 5).map(er => er.message).join('\n');
        const extra = errors.length > 5 ? `\n...共 ${errors.length} 項` : '';
        message.error({ content: summary + extra, duration: 6, style: { whiteSpace: 'pre-line' } });
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, details, setFieldErrors]);

  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await j209Api.delete(record.fj180_01);
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  const addDetailRow = useCallback(() => {
    const maxSeq = details.reduce((max, d) => Math.max(max, d.fj181_02 || 0), 0);
    if (maxSeq >= 99) {
      message.warning('明細最多 99 項');
      return;
    }
    setDetails(prev => [...prev, {
      fj181_02: maxSeq + 1,
      fj181_01: formData.fj180_01 || '',
      fj181_10: (formData.fj180_15 || '') as string,
      fj181_11: '',
      fj181_12: '',
      fj181_16: '',
      fj181_27: '',
      fj181_19: '',
      fj181_20: '',
      fj181_15: 0,
      fj181_18: 1,
    }]);
  }, [details, formData.fj180_01, formData.fj180_15]);

  const deleteDetailRow = useCallback(() => {
    if (details.length <= 0) return;
    setDetails(prev => {
      const next = prev.filter((_, i) => i !== selectedRow);
      return next.map((d, i) => ({ ...d, fj181_02: i + 1 }));
    });
    setSelectedRow(Math.max(0, selectedRow - 1));
  }, [details, selectedRow]);

  const revertToRecord = useCallback(() => {
    clearFieldErrors();
    if (record) {
      setFormData({ ...record });
      setDetails(record.details || []);
    }
  }, [record, clearFieldErrors]);

  const resetForAdd = useCallback(() => {
    clearFieldErrors();
    setRecord(null);
    const today = new Date().toISOString().substring(0, 10) + 'T00:00:00Z';
    setFormData({
      fj180_80: '',
      fj180_18: 0,
      fj180_11: today,
    });
    setDetails([]);
    setSelectedRow(0);
  }, [clearFieldErrors]);

  const applyResult = useCallback((result: FJ180) => {
    setRecord(result);
    setFormData({ ...result });
    setDetails(result.details || []);
  }, []);

  return {
    record, formData, details, selectedRow, loading,
    setSelectedRow, setDetails,
    loadRecord, setField, getField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    revertToRecord, resetForAdd, applyResult,
    getFieldError, clearFieldErrors,
  };
}
